import { AfterViewInit, Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { NavbarComponent } from './components/navbar/navbar.component';
import { AboutUsComponent } from './components/about-us/about-us.component';
import { HomeComponent } from './components/home/home.component';
import { BlogComponent } from './components/blog/blog.component';
import { ContactComponent } from "./components/contact/contact.component";
import { ImprintComponent } from './components/imprint/imprint.component';
import { PrivacyPolicyComponent } from './components/privacy-policy/privacy-policy.component';
import { DockComponent } from './components/dock/dock.component';
import { ResumeComponent } from './components/resume/resume.component';
import { PortfolioComponent } from './components/portfolio/portfolio.component';
import { ActiveFaces, CubeService } from './services/cube.service';
import { CommonModule } from '@angular/common';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { ThemeService } from './services/theme.service';
import { ScrollStateService } from './services/scroll-state.service';
import { OverlayKey, OverlayStateService } from './services/overlay-state.service';
import { TranslationSyncService } from './services/translation-sync.service';

type OverlayAnim = 'idle' | 'expand' | 'shrink';

@Component({
  selector: 'app-root',
  imports: [
    NavbarComponent, AboutUsComponent,
    HomeComponent, BlogComponent,
    ContactComponent, DockComponent, ResumeComponent, PortfolioComponent, ImprintComponent,
    CommonModule,
    PrivacyPolicyComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, AfterViewInit {
  // @ViewChild('dock', { read: ElementRef }) dockRef!: ElementRef<HTMLElement>;
  // @ViewChild('footer', { read: ElementRef }) footer!: ElementRef<HTMLElement>;
  // @ViewChild('header', { read: ElementRef }) headerRef!: ElementRef<HTMLElement>;

  @ViewChild(NavbarComponent) navbar?: NavbarComponent;
  @ViewChild(DockComponent) dock?: DockComponent;
  @ViewChild(DockComponent, { read: ElementRef }) appDock?: ElementRef<HTMLElement>;

  rotationY = 0;
  rotationX = 0;
  title: any;
  isImprintVisible = false;
  isPrivacyPolicyVisible = false;
  activeFaces$: Observable<ActiveFaces>;
  isDarkMode: boolean;
  activeFace: number = 0;
  hiddenFaces: number[] = [];
  private dockHideTimeout?: number;
  private dockShowTimeout?: number;

  private lastTop = new WeakMap<HTMLElement, number>();
  private rafLock = new WeakMap<HTMLElement, boolean>();


  imprintAnim: OverlayAnim = 'idle';     // steuert .expand / .shrink
private overlaySub?: Subscription;


  navbarHidden = true;

  constructor(
    public cubeService: CubeService
    , private themeService: ThemeService
    , public scroll: ScrollStateService
    , public overlayState: OverlayStateService
    , public translate : TranslationSyncService
  ) {

    this.themeService.isDarkMode$.subscribe(mode => {

      this.isDarkMode = mode;
      Promise.resolve().then(() => {
        const triggeredByToggle = this.themeService.wasThemeToggled();
        this.overlayState.setDarkMode(this.isDarkMode, triggeredByToggle);
      });
    });
    this.activeFaces$ = cubeService.activeFaces$;
  }

  ngOnInit() {
    this.initializeDarkContainers(); // Dark-Container für jede Seite einmalig erstellen
    document.addEventListener('imprintToggle', () => {
      this.toggleImprint();
    });

    document.addEventListener('privacyToggle', () => {
      this.togglePrivacyPolicy();
    });

      this.overlaySub = this.overlayState.state$.subscribe(s => {
    const imprintActive = s.overlays.imprint.phase === 'expanding' || s.overlays.imprint.phase === 'expanded';
    const privacyActive = s.overlays.privacy.phase === 'expanding' || s.overlays.privacy.phase === 'expanded';

    this.syncFooterActive(imprintActive, privacyActive);

    // optional:
    this.syncFooterDisabled(s.overlays.imprint.busy, s.overlays.privacy.busy);
  });
}


  ngOnDestroy() {
  this.overlaySub?.unsubscribe();
}

async ngAfterViewInit() {
  const state = localStorage.getItem('cubeState');
  const currentIndex = state ? JSON.parse(state).currentIndex : 0;

  this.cubeService.lockCube();

  await this.delayFrames(4);   // 🔥 4 Frames warten (~60–70ms)

  if (currentIndex == 0) {
    setTimeout(() => {
      this.cubeService.setActiveFace(currentIndex);
      this.cubeService.unlockCube();
    }, 5000);
  } else {
    setTimeout(() => {
      this.cubeService.setActiveFace(currentIndex);
      setTimeout(() => this.cubeService.unlockCube(), 2000);
    }, 6000);
  }

  this.overlayState.state$.subscribe(() => this.toggleDockVisibility());
  this.initStageSizing();
  this.initDockHideOnDownScroll();
}

private delayFrames(count: number = 3): Promise<void> {
  return new Promise(resolve => {
    const step = () => {
      if (--count <= 0) resolve();
      else requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
}

// private async preloadTranslations(): Promise<void> {
//   const lang = this.translate.currentLang || this.translate.defaultLang || 'de';
//   try {
//     await firstValueFrom(this.translate.getTranslation(lang));
//   } catch {
//     // not fatal – continue boot
//   }
// }

  onOverlayAnimEnd(
    key: OverlayKey,
    variant: 'light' | 'dark',
    e: AnimationEvent,
    contentEl: HTMLElement
  ): void {
    if (e.target !== contentEl) return;

    const ov = this.overlayState.snapshotOf(key);

    // Nur wenn dieses Event von der aktiven (oben liegenden) Variante kommt
    if (variant !== ov.activeVariant) return;

    const name = e.animationName;

    if (name.endsWith('overlayExpand') && ov.phase === 'expanding') {
      this.overlayState.onAnimationEnd(key, 'expand');
    }

    if (name.endsWith('overlayShrink') && ov.phase === 'shrinking') {
      this.overlayState.onAnimationEnd(key, 'shrink');
    }
  }

  onOverlayRootAnimEnd(
    key: OverlayKey,
    variant: 'light' | 'dark',
    e: AnimationEvent
  ): void {
    if (e.animationName !== 'wipe') return;

    const ov = this.overlayState.snapshotOf(key);
    if (!ov.visible) return;

    // sicherstellen, dass es wirklich vom Root kommt (nicht bubbled)
    const expectedId = variant === 'light' ? ov.ids.light : ov.ids.dark;
    const el = e.currentTarget as HTMLElement | null;
    if (!el || el.id !== expectedId) return;

    this.overlayState.ackWipe(key);
  }



  toggleImprint(): void {
    this.overlayState.toggle('imprint');
    // this.overlayState.logSnapshot('AFTER TOGGLE', 'imprint');
  }

  togglePrivacyPolicy(): void {
    this.overlayState.toggle('privacy');
    this.overlayState.logSnapshot('AFTER TOGGLE', 'privacy');
  }

  closeImprint(): void {
    this.overlayState.close('imprint');
    this.overlayState.logSnapshot('AFTER CLOSE', 'imprint');
  }

  closePrivacyPolicy(): void {
    this.overlayState.close('privacy');
    this.overlayState.logSnapshot('AFTER CLOSE', 'privacy');
  }

  /**
   * Muss im Template am Overlay hängen:
   * (animationend)="onImprintAnimationEnd($event)"
   */
  onImprintAnimationEnd(e: AnimationEvent): void {
    if (e.animationName === 'overlayExpand') {
      // Expand fertig -> expand-Klasse weg
      this.imprintAnim = 'idle';
    }

    if (e.animationName === 'overlayShrink') {
      // Shrink fertig -> jetzt erst unsichtbar machen
      this.imprintAnim = 'idle';
      this.isImprintVisible = false;
    }
  }

  initializeDarkContainers() {
    // const pages = ['home', 'about', 'resume', 'portfolio', 'blog', 'contact'];
    const pages = ['home', 'about', 'portfolio', 'blog', 'contact'];

    pages.forEach(page => {
      const pageContainer = document.querySelector(`.box`) as HTMLElement;

      if (!pageContainer) return;

      if (!document.querySelector(`#dark-container-${page}`)) {

        const cloneContainer = pageContainer.querySelector(`.face.${page}`).querySelector(`.section.${page}`).cloneNode(true) as HTMLElement;
        cloneContainer.id = `dark-container-${page}`;
        cloneContainer.classList.remove('active');
        pageContainer.querySelector(`.face.${page}`).appendChild(cloneContainer);
      }
    });
    this.addDarkContainerListeners();
    // this.attachDarkScrollBridge();
    const darkContainerImg = document.querySelector('#dark-container-home .home-img img') as HTMLImageElement;
    darkContainerImg.src = 'home/hero_dark.webp';
  }




  private toggleDockVisibility(): void {
    const dockEl = this.appDock.nativeElement;
    if (!dockEl) return;

    const { imprint, privacy } = this.overlayState.snapshot.overlays;
    const anyOverlayVisible = imprint.visible || privacy.visible;

    // Alte Timer aufräumen
    window.clearTimeout(this.dockHideTimeout);
    window.clearTimeout(this.dockShowTimeout);

    if (anyOverlayVisible) {
      // 👉 Dock schnell verstecken
      this.dockHideTimeout = window.setTimeout(() => {
        dockEl.classList.add('hidden');
      }, 100);
    } else {
      // 👉 Dock verzögert wieder anzeigen
      this.dockShowTimeout = window.setTimeout(() => {
        dockEl.classList.remove('hidden');
      }, 400); // bewusst kürzer als vorher
    }
  }

addDarkContainerListeners() {
  console.log('feuerst dz denn');

  const darkFooterButtons = document.querySelectorAll<HTMLButtonElement>(
    '[id^="dark-container-"] .footer .footer-link'
  );

  darkFooterButtons.forEach(btn => {
    if (btn.dataset["bound"] === '1') return;     // ✅ schon gebunden
    btn.dataset["bound"] = '1';

    const target = btn.getAttribute('aria-controls');
      // this.attachDarkDockScrollBridge();

    switch (target) {
      case 'imprint-overlay':
        btn.addEventListener('click', () => this.toggleImprint());
        break;
      case 'privacy-policy-overlay':
        btn.addEventListener('click', () => this.togglePrivacyPolicy());
        break;
    }
  });

}

  private ro?: ResizeObserver;
  private cleanupFns: Array<() => void> = [];

  private initStageSizing(): void {
    const root = document.documentElement;
    const update = () => {
      const h = this.navbar?.getHeaderElement();
      const d = this.dock?.getDockElement();

      const navH = h ? Math.round(h.getBoundingClientRect().height) : 0;
      const dockH = d ? Math.round(d.getBoundingClientRect().height) : 0;

      root.style.setProperty('--nav-h', `${navH}px`);
      root.style.setProperty('--dock-h', `${dockH}px`);

    };

    // ✅ 1) nach Render: 2 Frames warten (Font/layout settling)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        update();

        // ✅ 2) ResizeObserver direkt auf header + dock
        this.ro?.disconnect();
        this.ro = new ResizeObserver(() => update());
        const h = this.navbar?.getHeaderElement();
        const d = this.dock?.getDockElement();
        if (h) this.ro.observe(h);
        if (d) this.ro.observe(d);
      });
    });

    // ✅ 3) window resize
    const onResize = () => update();
    window.addEventListener('resize', onResize, { passive: true });
    this.cleanupFns.push(() => window.removeEventListener('resize', onResize));

    // ✅ 4) visualViewport (DevTools device toggles!)
    if (window.visualViewport) {
      const onVV = () => update();
      window.visualViewport.addEventListener('resize', onVV, { passive: true });
      window.visualViewport.addEventListener('scroll', onVV, { passive: true });
      this.cleanupFns.push(() => {
        window.visualViewport?.removeEventListener('resize', onVV);
        window.visualViewport?.removeEventListener('scroll', onVV);
      });
    }

    // ✅ 5) Wenn navbarHidden toggelt, kann height 0 werden → nachziehen
    // (falls du die hidden-Klasse nutzt, die height beeinflusst)
    // Hier reicht: nach jedem overlay/nav toggle einmal update() triggern.
  }

  private initDockHideOnDownScroll(): void {
  const handler = (ev: Event) => {
    const scroller = this.pickScroller(ev.target as HTMLElement | null);
    if (!scroller) return;

    if (this.rafLock.get(scroller)) return;
    this.rafLock.set(scroller, true);

    requestAnimationFrame(() => {
      const now = scroller.scrollTop ?? 0;
      const prev = this.lastTop.get(scroller) ?? 0;
      const delta = now - prev;

      if (delta > 6) {
        this.hideDock();      // ↓ runter
      } else if (delta < -6) {
        this.showDock();      // ↑ hoch
      }

      this.lastTop.set(scroller, now);
      this.rafLock.set(scroller, false);
    });
  };

  document.addEventListener('scroll', handler, { passive: true, capture: true } as any);
  this.cleanupFns.push(() =>
    document.removeEventListener('scroll', handler, { capture: true } as any)
  );
}


private hideDock(): void {
  this.appDock.nativeElement.classList.add('hidden');
}

private showDock(): void {
  this.appDock?.nativeElement.classList.remove('hidden');
}

private pickScroller(el: HTMLElement | null): HTMLElement | null {
  if (!el) return null;
  // passe die Reihenfolge an dein Projekt an – kurz gehalten
  return (
    el.closest('.tab-wrapper') ??
    el.closest('.section.active') ??
    null
  );
}

private syncFooterActive(imprintActive: boolean, privacyActive: boolean) {
  const btns = document.querySelectorAll<HTMLButtonElement>(
    '.footer .footer-link' // ✅ trifft Light + Dark (weil beide haben .footer)
  );

  btns.forEach(btn => {
    const target = btn.getAttribute('aria-controls');
    if (target === 'imprint-overlay') {
      btn.classList.toggle('active', imprintActive);
    }
    if (target === 'privacy-policy-overlay') {
      btn.classList.toggle('active', privacyActive);
    }
  });
}

private syncFooterDisabled(imprintBusy: boolean, privacyBusy: boolean) {
  const btns = document.querySelectorAll<HTMLButtonElement>('.footer .footer-link');

  btns.forEach(btn => {
    const target = btn.getAttribute('aria-controls');
    if (target === 'imprint-overlay') {
      btn.toggleAttribute('disabled', imprintBusy);
    }
    if (target === 'privacy-policy-overlay') {
      btn.toggleAttribute('disabled', privacyBusy);
    }
  });
}


}
